import React from "react";
import { BoxArrowDown } from "@phosphor-icons/react";
import { Icon } from "@shopflo/ui/icon";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { usePopupOrchestrator } from "lib/contexts/PopupOrchestratorProvider";
import { useSubscriptionContext } from "lib/contexts/SubscriptionProvider";
import { useLocale } from "lib/hooks/useLocale";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { analyticsEvents } from "lib/utils/constants";
import PlanRow from "components/subscriptions/PlanRow";
import OverlaySpinner from "components/common/loaders/OverlaySpinner";

const ChoosePlanDialog: React.FC = () => {
  const { t } = useLocale();
  const {
    state: { plans, selectedPlanId, isSwitching },
    actions: { selectPlan },
  } = useSubscriptionContext();
  const {
    state: { checkoutMetadata },
  } = useMerchantContext();
  const { closePopup } = usePopupOrchestrator();
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();

  const config = checkoutMetadata?.uiAttributes?.subscriptionPlanConfig;

  const handleSelect = async (planId: string) => {
    if (isSwitching) return;

    if (planId === selectedPlanId) {
      closePopup();
      return;
    }

    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_SUBSCRIPTION_PLAN_SELECTED,
      eventType: "click",
      metaData: { planId },
    });

    await selectPlan(planId);
    closePopup();
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center gap-3 px-5 py-4">
        <span>
          <Icon
            IconComponent={BoxArrowDown}
            size="xl"
            className="text-coal-medium opacity-60"
            iconProps={{ weight: "fill" }}
          />
        </span>
        <div className="flex flex-col">
          <h2 className="text-text-md-semibold text-zinc-800">{t("subscription_choose_plan")}</h2>
          <p className="text-text-xs-medium text-zinc-800/60">{t("subscription_choose_plan_subtitle")}</p>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-2 mb-12">
        {plans.map((plan) => (
          <PlanRow
            key={plan.id}
            plan={plan}
            isSelected={plan.id === selectedPlanId}
            isDisabled={isSwitching}
            onSelect={handleSelect}
          />
        ))}
      </div>

      {isSwitching && <OverlaySpinner />}
    </div>
  );
};

export default ChoosePlanDialog;
